import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Loader2,
  UploadCloud,
  FileText,
  Download,
  Sparkles,
  Calendar,
  User as UserIcon,
  History,
  ShieldCheck,
  Briefcase,
  Building2,
  UserCog,
  Users,
  ChevronDown,
  ChevronRight,
  Link2,
  RefreshCw,
  Wand2,
  CheckCircle2,
  Search,
  X,
} from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { useUserProfileContext } from '../context/UserProfileContext';
import {
  getCvOverview,
  uploadCv,
  downloadCv,
  triggerBrowserDownload,
} from '../utils/userCvApi';
import { UserCvOverview, UserCvVersion } from '../types/userCv';
import { useToast } from '../hooks/useToast';
import { Toast } from '../components/Toast';
import { Pagination } from '../components/common/Pagination';
import { CvUpdateReviewPanel } from '../components/CvUpdateReviewPanel';
import { formatDateInIST, mapApiFeedbackRequestsToList } from '../utils/mappers';
import { FeedbackRequest } from '../types/feedback';
import { envConfig } from '../config/authConfig';

const ACCEPTED_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const MAX_BYTES = 5 * 1024 * 1024;
const HISTORY_PAGE_SIZE = 10;

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(s: string) {
  return formatDateInIST(s);
}

function formatConnectLabel(v: UserCvVersion): string | null {
  if (v.source !== 'ai-update') return null;
  const period = v.sourceConnectQuarter && v.sourceConnectYear
    ? `${v.sourceConnectQuarter} ${v.sourceConnectYear}`
    : null;
  const name = v.sourceConnectName?.trim();
  if (name && period) return `${name} · ${period}`;
  return name || period || (v.sourceConnectId ? `Connect #${v.sourceConnectId}` : null);
}

export function ProfilePage() {
  const { apiCall, getAccessToken } = useApiClient();
  const {
    userProfile,
    loading: profileLoading,
    error: profileError,
    fetchUserProfile,
  } = useUserProfileContext();
  const { toasts, hideToast, success: toastOk, error: toastErr } = useToast();

  const [overview, setOverview] = useState<UserCvOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const fileRef = useRef<HTMLInputElement>(null);

  // Completed Connects picker state
  const [connects, setConnects] = useState<FeedbackRequest[]>([]);
  const [connectsLoading, setConnectsLoading] = useState(false);
  const [selectedConnectIds, setSelectedConnectIds] = useState<Set<number>>(new Set());
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDraft, setPickerDraft] = useState<Set<number>>(new Set());
  const [pickerQuery, setPickerQuery] = useState('');

  const employeeId = userProfile?.employeeId;

  const loadOverview = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const data = await getCvOverview(employeeId, apiCall);
      setOverview(data);
    } catch (e: any) {
      toastErr(e?.message ?? 'Failed to load CV');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
    loadConnects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const loadConnects = async () => {
    if (!employeeId) return;
    setConnectsLoading(true);
    try {
      const res = await apiCall(
        `${envConfig.apiBaseUrl}/api/FeedbackRequests/my-feedback/${encodeURIComponent(employeeId)}`
      );
      const json = await res.json();
      const list: any[] = json?.data ?? json ?? [];
      const mapped = mapApiFeedbackRequestsToList(list);
      // Completed only, newest first
      const completed = mapped
        .filter(r => r.status === 'completed' && r.canUpdateCv)
        .sort((a, b) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime());
      setConnects(completed);
    } catch (e) {
      // Non-fatal: section just won't list any connects.
      setConnects([]);
    } finally {
      setConnectsLoading(false);
    }
  };

  const validateFile = (f: File): string | null => {
    if (!ACCEPTED_TYPES.includes(f.type)) return 'Only PDF or DOCX files are allowed.';
    if (f.size > MAX_BYTES) return 'Maximum file size is 5 MB.';
    return null;
  };

  const handleFile = async (file: File | null) => {
    if (!file || !employeeId || !userProfile) return;
    const err = validateFile(file);
    if (err) { toastErr(err); return; }
    setUploading(true);
    try {
      await uploadCv(employeeId, file, userProfile.email, getAccessToken);
      toastOk(`Uploaded ${file.name}`);
      await loadOverview();
    } catch (e: any) {
      toastErr(e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (v: UserCvVersion, format: 'original' | 'docx') => {
    if (!employeeId) return;
    const key = `${v.id}-${format}`;
    setDownloadingId(key);
    try {
      const { blob, fileName } = await downloadCv(employeeId, v.version, format, getAccessToken);
      triggerBrowserDownload(blob, fileName);
    } catch (e: any) {
      toastErr(e?.message ?? 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = (e: React.DragEvent) => { e.preventDefault(); setDragActive(false); };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const history = overview?.history ?? [];
  const totalHistoryPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const visibleHistory = useMemo(
    () => history.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE),
    [history, historyPage]
  );

  if (!userProfile) {
    if (!profileLoading && profileError) {
      return (
        <div className="min-h-screen bg-slate-50 pt-20 flex flex-col items-center justify-center gap-4 px-6 text-center">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Unable to load profile</h1>
            <p className="mt-1 text-sm text-slate-600">{profileError}</p>
          </div>
          <button
            type="button"
            onClick={() => fetchUserProfile()}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
          >
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50 pt-20 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading profile…
      </div>
    );
  }

  const directReportsCount = userProfile.directReports?.length ?? 0;

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <main className="mx-auto px-6 lg:px-8 py-6 max-w-screen-2xl">
        {/* Identity strip — avatar + name on top, 4 info cells below.
            Replaces the previous pink-gradient hero + separate InfoCard grid
            for a calmer, more "app-like" profile header. */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="p-6 flex items-center gap-5 flex-wrap">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-white shadow-md">
              {userProfile.photoUrl ? (
                <img src={userProfile.photoUrl} alt={userProfile.displayName} className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-8 h-8 text-white" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-slate-900 leading-tight truncate" title={userProfile.displayName}>
                {userProfile.displayName}
              </h1>
              <div className="mt-1 flex items-center gap-2 flex-wrap text-sm text-slate-500">
                <span className="truncate">{userProfile.email}</span>
                <span className="text-slate-300">·</span>
                <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                  {userProfile.employeeId}
                </span>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            <IdentityInfoCell
              icon={<Briefcase className="w-4 h-4" />}
              tone="blue"
              label="Job Title"
              value={userProfile.jobTitle || '—'}
            />
            <IdentityInfoCell
              icon={<Building2 className="w-4 h-4" />}
              tone="indigo"
              label="Department"
              value={userProfile.department || '—'}
            />
            <IdentityInfoCell
              icon={<UserCog className="w-4 h-4" />}
              tone="slate"
              label="Manager"
              value={userProfile.manager?.displayName || '—'}
              sub={userProfile.manager?.jobTitle}
            />
            <IdentityInfoCell
              icon={<Users className="w-4 h-4" />}
              tone="emerald"
              label="Direct Reports"
              value={`${directReportsCount}`}
              sub={directReportsCount === 1 ? '1 reportee' : `${directReportsCount} reportees`}
            />
          </div>
        </section>

        {/* CV Section */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2.5">
                <span className="inline-flex w-8 h-8 rounded-lg bg-blue-50 text-blue-600 items-center justify-center">
                  <FileText className="w-4 h-4" />
                </span>
                My CV
              </h2>
              <p className="text-sm text-slate-500 mt-1 ml-[42px]">
                Upload your resume in PDF or DOCX. You can update it from any completed Connect using AI.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {overview?.latest && (
                <>
                  <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
                    Latest: v{overview.latest.version}
                  </div>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50"
                    title="Re-upload a new CV file (PDF or DOCX, max 5 MB)"
                  >
                    {uploading
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5" />}
                    {uploading ? 'Uploading…' : 'Re-upload CV'}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                  />
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : overview?.latest ? (
            <LatestCvCard
              version={overview.latest}
              onDownload={handleDownload}
              downloadingId={downloadingId}
            />
          ) : (
            <EmptyCvBanner />
          )}

          {/* Upload area — only shown when there is NO existing CV. Once a CV exists,
              the small “Re-upload CV” button at the top of the card handles new uploads. */}
          {!overview?.latest && (
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`mt-5 border-2 border-dashed rounded-2xl px-6 py-8 text-center transition-colors ${
                dragActive ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50'
              }`}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
              <UploadCloud className={`w-8 h-8 mx-auto mb-2 ${dragActive ? 'text-blue-600' : 'text-slate-400'}`} />
              <p className="text-sm font-medium text-slate-700">
                {uploading ? 'Uploading…' : 'Drag & drop your CV here, or click to browse'}
              </p>
              <p className="text-xs text-slate-400 mt-1">PDF or DOCX · max 5 MB</p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50 transition-all"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                {uploading ? 'Uploading…' : 'Choose file'}
              </button>
            </div>
          )}

          {/* Update CV from completed Connects (opens picker modal) */}
          {overview?.latest && (
            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                setPickerDraft(new Set(selectedConnectIds));
                setPickerQuery('');
                setPickerOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPickerDraft(new Set(selectedConnectIds));
                  setPickerQuery('');
                  setPickerOpen(true);
                }
              }}
              className="mt-6 border-t border-slate-100 pt-5 -mx-5 px-5 cursor-pointer rounded-b-2xl hover:bg-slate-50/70 focus:outline-none focus:bg-blue-50/40 transition-colors"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <Wand2 className="w-4 h-4 text-indigo-600" /> Update CV from completed Connects
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Pick one or more completed Connects and let AI fold the highlights into your CV.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPickerDraft(new Set(selectedConnectIds));
                    setPickerQuery('');
                    setPickerOpen(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                >
                  <Sparkles className="w-4 h-4" /> Update CV
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Collapsible version history */}
        {history.length > 1 && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <button
              onClick={() => setHistoryOpen(o => !o)}
              className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                {historyOpen
                  ? <ChevronDown className="w-5 h-5 text-slate-500" />
                  : <ChevronRight className="w-5 h-5 text-slate-500" />}
                <span className="inline-flex w-8 h-8 rounded-lg bg-slate-100 text-slate-600 items-center justify-center">
                  <History className="w-4 h-4" />
                </span>
                <h2 className="text-lg font-bold text-slate-900">Version history</h2>
                <span className="ml-1 px-2 py-0.5 text-xs font-bold rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                  {history.length}
                </span>
              </div>
              <span className="text-xs text-slate-400">{historyOpen ? 'Click to collapse' : 'Click to expand'}</span>
            </button>

            {historyOpen && (
              <div className="border-t border-slate-100">
                <div className="divide-y divide-slate-100">
                  {visibleHistory.map((v) => (
                    <VersionRow
                      key={v.id}
                      version={v}
                      onDownload={handleDownload}
                      downloadingId={downloadingId}
                    />
                  ))}
                </div>
                <Pagination
                  currentPage={historyPage}
                  totalPages={totalHistoryPages}
                  onPageChange={setHistoryPage}
                  totalItems={history.length}
                  itemsPerPage={HISTORY_PAGE_SIZE}
                />
              </div>
            )}
          </section>
        )}

        {/* Toasts */}
        {toasts.map((t) => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => hideToast(t.id)} />
        ))}
      </main>

      {reviewOpen && employeeId && (
        <CvUpdateReviewPanel
          employeeId={employeeId}
          connects={connects
            .filter(c => selectedConnectIds.has(c.id))
            .map(c => ({
              id: c.id,
              label:
                (c.connectName ? c.connectName : null) ??
                (c.quarter && c.year ? `${c.quarter} ${c.year}` : `Connect #${c.id}`),
            }))}
          onClose={() => setReviewOpen(false)}
          onSuccess={(newVersion) => {
            setReviewOpen(false);
            setSelectedConnectIds(new Set());
            toastOk(`CV updated — saved as v${newVersion}`);
            loadOverview();
          }}
        />
      )}

      {/* Connect picker modal */}
      {pickerOpen && (
        <ConnectPickerModal
          connects={connects}
          loading={connectsLoading}
          selected={pickerDraft}
          query={pickerQuery}
          onQueryChange={setPickerQuery}
          onToggle={(id) => {
            setPickerDraft(s => {
              const next = new Set(s);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            });
          }}
          onCancel={() => setPickerOpen(false)}
          onConfirm={() => {
            setSelectedConnectIds(new Set(pickerDraft));
            setPickerOpen(false);
            setReviewOpen(true);
          }}
        />
      )}
    </div>
  );
}

interface PickerConnect {
  id: number;
  connectName?: string;
  quarter?: string | number;
  year?: string | number;
  requestDate?: string;
}

function ConnectPickerModal({
  connects, loading, selected, query, onQueryChange, onToggle, onCancel, onConfirm,
}: {
  connects: PickerConnect[];
  loading: boolean;
  selected: Set<number>;
  query: string;
  onQueryChange: (v: string) => void;
  onToggle: (id: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Sort by completion / request date desc (already sorted upstream but be defensive).
  const sorted = useMemo(() => {
    return [...connects].sort((a, b) => {
      const ta = a.requestDate ? Date.parse(a.requestDate) : 0;
      const tb = b.requestDate ? Date.parse(b.requestDate) : 0;
      return tb - ta;
    });
  }, [connects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(c => {
      const label = (c.connectName || `${c.quarter ?? ''} ${c.year ?? ''}`).toLowerCase();
      const date = c.requestDate ? formatDateInIST(c.requestDate).toLowerCase() : '';
      return label.includes(q) || date.includes(q) || String(c.id).includes(q);
    });
  }, [sorted, query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 flex flex-col max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-indigo-600" /> Select completed Connects
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Choose one or more to feed into the AI CV update.</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search by name, quarter or date…"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading Connects…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              {connects.length === 0 ? 'No completed Connects yet.' : 'No Connects match your search.'}
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
              {filtered.map(c => {
                const checked = selected.has(c.id);
                const label = c.connectName || `${c.quarter ?? ''} ${c.year ?? ''}`.trim() || `Connect #${c.id}`;
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                      checked ? 'bg-blue-50/60' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(c.id)}
                      className="accent-blue-600 w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">{label}</span>
                        {c.quarter && c.year && (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                            {c.quarter} {c.year}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3" /> Completed
                        </span>
                      </div>
                      {c.requestDate && (
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {formatDateInIST(c.requestDate)}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <span className="text-xs text-slate-500">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={selected.size === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-4 h-4" /> Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IdentityInfoCell({
  icon, tone, label, value, sub,
}: {
  icon: React.ReactNode;
  tone: 'blue' | 'indigo' | 'slate' | 'emerald';
  label: string;
  value: string;
  sub?: string;
}) {
  const toneClass = {
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    slate: 'bg-slate-100 text-slate-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  }[tone];
  return (
    <div className="p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg ${toneClass} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500">{label}</p>
        <p className="text-sm font-semibold text-slate-900 truncate mt-0.5" title={value}>{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5 truncate" title={sub}>{sub}</p>}
      </div>
    </div>
  );
}

function LatestCvCard({
  version, onDownload, downloadingId,
}: {
  version: UserCvVersion;
  onDownload: (v: UserCvVersion, fmt: 'original' | 'docx') => void;
  downloadingId: string | null;
}) {
  const sourceBadge = version.source === 'ai-update'
    ? <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border bg-purple-50 text-purple-700 border-purple-200"><Sparkles className="w-3 h-3" /> AI updated</span>
    : <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border bg-slate-100 text-slate-600 border-slate-200"><ShieldCheck className="w-3 h-3" /> Uploaded</span>;
  const connectLabel = formatConnectLabel(version);
  const isDownloading = downloadingId === `${version.id}-original`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onDownload(version, 'original')}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDownload(version, 'original'); } }}
      title={`Download ${version.fileName}`}
      className="group bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-5 flex items-center gap-4 flex-wrap cursor-pointer hover:from-blue-100 hover:to-indigo-100 hover:border-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
    >
      <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-blue-600 flex-shrink-0 shadow-sm">
        <FileText className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 truncate" title={version.fileName}>{version.fileName}</span>
          {sourceBadge}
        </div>
        <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(version.createdAt)}</span>
          <span>{formatBytes(version.fileSize)}</span>
          <span className="uppercase">{version.fileExtension}</span>
        </div>
        {connectLabel && (
          <p className="text-xs text-purple-700 mt-1.5 flex items-center gap-1">
            <Link2 className="w-3 h-3" />
            Updated from Connect: <span className="font-semibold">{connectLabel}</span>
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {version.hasDocx && (
          <DownloadButton v={version} format="docx" onDownload={onDownload} downloadingId={downloadingId} />
        )}
        <button
          type="button"
          onClick={() => onDownload(version, 'original')}
          disabled={isDownloading}
          title={`Download ${version.fileName}`}
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/70 text-blue-600 group-hover:bg-white shadow-sm disabled:opacity-50"
        >
          {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function VersionRow({
  version, onDownload, downloadingId,
}: {
  version: UserCvVersion;
  onDownload: (v: UserCvVersion, fmt: 'original' | 'docx') => void;
  downloadingId: string | null;
}) {
  const connectLabel = formatConnectLabel(version);
  const isDownloading = downloadingId === `${version.id}-original`;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onDownload(version, 'original')}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDownload(version, 'original'); } }}
      title={`Download ${version.fileName}`}
      className="group flex items-center gap-4 py-3 px-5 cursor-pointer hover:bg-slate-50 focus:outline-none focus:bg-blue-50/40 transition-colors"
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        version.source === 'ai-update' ? 'bg-purple-100 text-purple-600' : 'bg-slate-100 text-slate-500'
      }`}>
        {version.source === 'ai-update' ? <Sparkles className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-800 text-sm">v{version.version}</span>
          <span className="text-sm text-slate-700 truncate" title={version.fileName}>{version.fileName}</span>
          {version.isLatest && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 uppercase">Latest</span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {version.source === 'ai-update' ? 'AI-updated' : 'Uploaded'} · {formatDate(version.createdAt)} · {formatBytes(version.fileSize)}
        </div>
        {connectLabel && (
          <p className="text-xs text-purple-700 mt-1 flex items-center gap-1">
            <Link2 className="w-3 h-3" />
            Updated from Connect: <span className="font-semibold">{connectLabel}</span>
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {version.hasDocx && (
          <DownloadButton v={version} format="docx" onDownload={onDownload} downloadingId={downloadingId} />
        )}
        <button
          type="button"
          onClick={() => onDownload(version, 'original')}
          disabled={isDownloading}
          title={`Download ${version.fileName}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 group-hover:text-blue-600 group-hover:bg-white disabled:opacity-50"
        >
          {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function DownloadButton({
  v, format, onDownload, downloadingId, primary,
}: {
  v: UserCvVersion;
  format: 'original' | 'docx';
  onDownload: (v: UserCvVersion, fmt: 'original' | 'docx') => void;
  downloadingId: string | null;
  primary?: boolean;
}) {
  const key = `${v.id}-${format}`;
  const busy = downloadingId === key;
  const label = format === 'docx' ? 'DOCX' : (v.fileExtension || 'PDF').toUpperCase();
  const cls = primary
    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
    : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200';
  return (
    <button
      onClick={() => onDownload(v, format)}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50 ${cls}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

function EmptyCvBanner() {
  return (
    <div className="text-center bg-slate-50 border border-dashed border-slate-300 rounded-2xl py-10">
      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-sm font-semibold text-slate-600">No CV uploaded yet</p>
      <p className="text-xs text-slate-400 mt-1">Upload one below to get started.</p>
    </div>
  );
}
